import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'

export interface SignaturePadHandle {
  toBlob: () => Promise<Blob | null>
  clear: () => void
}

interface SignaturePadProps {
  onChange?: (hasStroke: boolean) => void
}

// Plain canvas draw, no external lib — port of jba-web's suratKuasaSignaturePad
// (auction_history.ctp) using Pointer Events instead of separate mouse/touch
// handlers, so a stroke keeps drawing even if the pointer leaves the canvas.
const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(({ onChange }, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const [hasStroke, setHasStroke] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!ctx) return
    ctx.strokeStyle = '#1a1a2e'
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
  }, [])

  const getPos = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    return {
      x: (evt.clientX - rect.left) * (canvas.width / rect.width),
      y: (evt.clientY - rect.top) * (canvas.height / rect.height),
    }
  }

  const handlePointerDown = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    evt.preventDefault()
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    canvas.setPointerCapture(evt.pointerId)
    drawingRef.current = true
    setHasStroke(true)
    onChange?.(true)

    const pos = getPos(evt)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }

  const handlePointerMove = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return
    evt.preventDefault()
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return

    const pos = getPos(evt)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
  }

  const handlePointerUp = () => {
    drawingRef.current = false
  }

  useImperativeHandle(ref, () => ({
    toBlob: () =>
      new Promise((resolve) => {
        const canvas = canvasRef.current
        if (!canvas || !hasStroke) {
          resolve(null)
          return
        }
        canvas.toBlob((blob) => resolve(blob), 'image/png')
      }),
    clear: () => {
      const canvas = canvasRef.current
      const ctx = canvas?.getContext('2d')
      if (canvas && ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      setHasStroke(false)
      onChange?.(false)
    },
  }))

  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-300 bg-white">
      {!hasStroke && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">
          Tanda tangan di sini
        </div>
      )}
      <canvas
        ref={canvasRef}
        width={500}
        height={180}
        className="h-[180px] w-full touch-none"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      />
    </div>
  )
})

SignaturePad.displayName = 'SignaturePad'

export default SignaturePad
